import formatPhone from 'src/functions/formatPhone'
import getLpuPhone from 'src/utils/getLpuPhone'
import getLpuAvatarUrl from 'src/utils/getLpuAvatarUrl'
import filterStarsPercent from 'common/filters/filterStarsPercent'
import type { Country } from 'src/types/country'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import type { PriceRangeTuple } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/price/_components/PriceSection/components/PriceHeader/types'

type LocalBusinessItempropsInput = {
  lpu: Lpu
  townName: string
  addressText?: string
  priceRange?: PriceRangeTuple
  currencyAbbreviation?: string
  country: Country
  httpHost?: string
  /** Абсолютный URL страницы — запасной origin для logo, если нет http_host. */
  pageUrl?: string
  ffUpdateLpuLogo?: boolean
  tStatic: (_key: string) => string
}

export type LocalBusinessItemprops = {
  telephone?: string
  addressLocality?: string
  streetAddress?: string
  priceRange?: string
  logo?: string
}

function resolveOrigin(httpHost?: string, pageUrl?: string): string | undefined {
  if (httpHost) {
    return `https://${httpHost}`
  }

  const match = pageUrl?.match(/^(https?:\/\/[^/]+)/)

  return match?.[1]
}

function toAbsoluteUrl(pathOrUrl: string, origin?: string): string {
  if (/^https?:\/\//.test(pathOrUrl)) {
    return pathOrUrl
  }

  if (!origin) {
    return pathOrUrl
  }

  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`

  return `${origin}${path}`
}

/**
 * Значения microdata / JSON-LD LocalBusiness.
 *
 * Адрес — PostalAddress (locality + street), как в legacy rates-блоке.
 * `priceRange` — «от N до M руб.,»; logo — абсолютный URL.
 */
export default function getLocalBusinessItemprops({
  lpu,
  townName,
  addressText,
  priceRange,
  currencyAbbreviation,
  country,
  httpHost,
  pageUrl,
  ffUpdateLpuLogo,
  tStatic,
}: LocalBusinessItempropsInput): LocalBusinessItemprops {
  const phone = getLpuPhone({
    lpu,
    lpuAddress: lpu.addr?.[0],
  })

  const telephone = phone
    ? formatPhone({
      phone,
      country,
    })
    : undefined

  const addressLocality = addressText ? townName : undefined
  const streetAddress = addressText || undefined

  let priceRangeText: string | undefined

  if (priceRange && priceRange[0] != null && priceRange[1] != null) {
    const currency = (currencyAbbreviation || 'руб').toLowerCase()

    priceRangeText = [
      tStatic('от'),
      priceRange[0],
      tStatic('до'),
      priceRange[1],
      `${currency}.,`,
    ].join(' ')
  }

  const logoPath = getLpuAvatarUrl({
    lpu,
    isLarge: true,
    ff_update_lpu_logo: ffUpdateLpuLogo,
  })
  const logo = logoPath
    ? toAbsoluteUrl(logoPath, resolveOrigin(httpHost, pageUrl))
    : undefined

  return {
    telephone,
    addressLocality,
    streetAddress,
    priceRange: priceRangeText,
    logo,
  }
}

/**
 * JSON-LD LocalBusiness для SSR (дублирует microdata для валидаторов).
 */
export function buildLocalBusinessJsonLd({
  name,
  url,
  itemprops,
  ratingValue,
  ratingCount,
}: {
  name: string
  url: string
  itemprops: LocalBusinessItemprops
  ratingValue?: number | string
  ratingCount?: number
}): Record<string, unknown> {
  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name,
    url,
  }

  if (itemprops.telephone) {
    jsonLd.telephone = itemprops.telephone
  }

  if (itemprops.logo) {
    jsonLd.logo = itemprops.logo
    jsonLd.image = itemprops.logo
  }

  if (itemprops.priceRange) {
    jsonLd.priceRange = itemprops.priceRange
  }

  if (itemprops.addressLocality || itemprops.streetAddress) {
    jsonLd.address = {
      '@type': 'PostalAddress',
      ...(itemprops.addressLocality
        ? { addressLocality: itemprops.addressLocality }
        : {}),
      ...(itemprops.streetAddress
        ? { streetAddress: itemprops.streetAddress }
        : {}),
    }
  }

  if (ratingCount && ratingCount > 0 && ratingValue != null) {
    jsonLd.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: String(ratingValue),
      bestRating: '100',
      worstRating: '0',
      ratingCount: String(ratingCount),
    }
  }

  return jsonLd
}

export function getLocalBusinessRatingValue(stars?: number | null): string {
  return String(filterStarsPercent(stars ?? 0))
}
